import { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import { useTranslation } from 'react-i18next'
import type { StorefrontProduct } from '../../shared/types'

/**
 * Radio picker for a product's preferences: one required single-select group per preference (D-073).
 * `initial` pre-selects a line's existing choices (used from the cart's "+", so tapping Add there
 * adds one more of the same variant); with no `initial` each group defaults to its first option.
 */
export default function OptionPicker({
  product,
  initial,
  onClose,
  onAdd,
}: {
  product: StorefrontProduct
  initial?: Record<string, string>
  onClose: () => void
  onAdd: (selections: Record<string, string>) => void
}) {
  const { t } = useTranslation()
  const groups = product.optionGroups ?? []
  const [selections, setSelections] = useState<Record<string, string>>(() =>
    Object.fromEntries(groups.map((g) => [g.name, initial?.[g.name] ?? g.options[0]])),
  )
  const complete = groups.every((g) => selections[g.name])

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{product.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {groups.map((g) => (
            <FormControl key={g.name} required>
              <FormLabel sx={{ fontWeight: 700 }}>{g.name}</FormLabel>
              <RadioGroup
                row
                value={selections[g.name] ?? ''}
                onChange={(e) => setSelections((prev) => ({ ...prev, [g.name]: e.target.value }))}
                sx={{ gap: 1, mt: 0.5 }}
              >
                {g.options.map((o) => {
                  const selected = selections[g.name] === o
                  return (
                    <FormControlLabel
                      key={o}
                      value={o}
                      control={<Radio sx={{ display: 'none' }} />}
                      label={o}
                      sx={{
                        m: 0,
                        px: 1.75,
                        py: 0.75,
                        borderRadius: '999px',
                        border: '1.5px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.main' : 'background.paper',
                        color: selected ? 'primary.contrastText' : 'text.primary',
                        boxShadow: selected ? 3 : 1,
                        transition: 'all .15s',
                        '& .MuiFormControlLabel-label': { fontWeight: 600 },
                      }}
                    />
                  )
                })}
              </RadioGroup>
            </FormControl>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!complete} onClick={() => onAdd(selections)}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
